import React, { useState, useEffect, useMemo, useRef } from "react";
import { Alert, Tooltip } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { resolveApiUrl } from "../../../assets/GlobalVariables";
import { getUserInfo } from "../../../services/localStorage";
import {
  getUserDetails,
} from "../../../services/authService";
import {
  getEntitlementsChipTierCssSuffix,
  useAgentEntitlements,
} from "../../../features/agentEntitlements";
import { useAgentBillingStatus } from "../../../contexts/AgentBillingStatusContext";
import { useAgentAccountOverview } from "../../../contexts/AgentAccountOverviewContext";
import { resolveAgentDisplayedPackage } from "../../../utils/resolveAgentDisplayedPackage";
import {
  agentPackageLabelHe,
  teamMemberPackageCodeForDisplay,
} from "../../../utils/agentPackageDisplay";
import { useOpenOffersCount } from "../../../hooks/useOpenOffersCount";
import {
  getAvailableOrdersCommission,
  getFinanceData,
  getFutureOrdersCommission,
  getOrdersCommissionHistory,
} from "../../../services/fhpService";
import {
  buildWalletSummary,
  flattenAvailableBucketsExcludingSystemBalance,
  sumCommissionsFromLines,
  traceWalletSsotSource,
} from "../../../utils/buildWalletSummary";
import {
  readHomeDashboardCache,
  resolveHomeDashboardCacheAgentKey,
  writeHomeDashboardCache,
} from "../../../utils/homeDashboardCache";
import {
  clearBillingExemptionDailyCache,
  localCalendarDayKey,
  msUntilNextLocalMidnight,
  readBillingExemptionDailyCache,
  resolveBillingExemptionCacheAgentKey,
  writeBillingExemptionDailyCache,
} from "../../../utils/billingExemptionDailyCache";
import { performPostFetch } from "../../../services/fetchService";
import {
  RECENT_ORDERS_HOME_FEED_ENDPOINT,
  buildRecentOrdersHomeFeedHeaders,
  parseRecentOrdersHomeFeedResponse,
  resolveHomeFeedSessionId,
  resolveHomeFeedViewerIsAdmin,
  traceRecentOrdersHomeFeed,
} from "../../../utils/recentOrdersHomeFeed";
import DashboardStats from "../DashboardStats/DashboardStats";
import RecentOrdersCard from "../RecentOrdersCard/RecentOrdersCard";
import OrdersCalendarCard from "../OrdersCalendarCard/OrdersCalendarCard";
import DashboardTasksCard from "../DashboardTasksCard/DashboardTasksCard";
import DashboardMyDucatsCard from "../DashboardMyDucatsCard/DashboardMyDucatsCard";
import {
  HotelCxlDropRiskBanner,
  useHotelCxlDropRiskAlerts,
  buildHotelCxlDropRiskOrdersPath,
} from "../../../features/hotelCxlDropRisk";
import {
  isLikelyLdsOrderRow,
  isLdsOrderBusinessSettled,
} from "../../../utils/ldsOrderStatus";
import { buildAgentPublicSiteUrl } from "../../../utils/publicSiteUrl";
import { navigateAgentPackageUpgrade } from "../../../utils/agentUpgradeNavigation";
import { useOperationRequestUi } from "../../../features/operationRequest/OperationRequestUiContext";
import { fetchAdminOperationRequests, fetchMyOperationRequests } from "../../../features/operationRequest/operationRequestsApi";
import {
  hasUnreadAdminReplyFromItem,
  hasUnreadAgentReplyFromItem,
} from "../../../features/operationRequest/agentOperationRequestListUtils";
import {
  buildCrmOpsDocketPath,
  navigateToCrmOpsDocket,
} from "../../Agent/Crm/navigateToCrmOpsDocket";
import OperationRequestHandlingTimer from "../../../features/operationRequest/components/OperationRequestHandlingTimer";
import BillingExemptionCard from "../../../Components/Agent/BillingExemptionCard/BillingExemptionCard";
import TopAgentsOfMonthCard from "../TopAgentsOfMonthCard/TopAgentsOfMonthCard";
import { resolvePublicSiteRefByAgentId } from "../../../services/publicSitesApi";
import { useRemindersHubOptional } from "../../../features/reminders/context/RemindersHubContext";
import "./DashboardPage.scss";

const ADMIN_ROLES = new Set(["admin", "super_admin"]);
const OPS_TERMINAL = new Set(["completed", "closed", "rejected", "cancelled"]);

/**
 * Temporary rollout: Top Agents of Month is admin-preview only.
 * Flip to false after prod QA to open for all agents.
 */
const TOP_AGENTS_OF_MONTH_ADMIN_ONLY = true;
/** Home recent feed — match visible list size (avoid fetching 100 for 15). */
const HOME_RECENT_ORDERS_LIMIT = 15;
/** Ducats — keep off the first connection burst. */
const HOME_SECONDARY_DEFER_MS = 2800;

/** Heavy widgets: only the active tab mounts and fetches. */
const HEAVY_TAB_RECENT = "recent";
const HEAVY_TAB_CALENDAR = "calendar";
const HEAVY_TAB_LEADERS = "leaders";

const EMPTY_STATS = {
  futureProfit: null,
  availableProfit: null,
  historyProfit: null,
  pendingOrders: null,
  openOrders: null,
};

function readInitialHomeCache() {
  try {
    const key = resolveHomeDashboardCacheAgentKey(getUserInfo(), getUserDetails());
    return readHomeDashboardCache(key);
  } catch {
    return null;
  }
}

function readInitialBillingExemptionCache() {
  try {
    const key = resolveBillingExemptionCacheAgentKey(
      getUserInfo(),
      getUserDetails()
    );
    return readBillingExemptionDailyCache(key);
  } catch {
    return null;
  }
}

const DashboardPage = () => {
  const navigate = useNavigate();
  const { openOperationRequest } = useOperationRequestUi();
  const remindersHub = useRemindersHubOptional();
  const cxlDropRisk = useHotelCxlDropRiskAlerts();
  const dashboardUser = getUserDetails();
  const [adminOpenOps, setAdminOpenOps] = useState([]);
  const [adminOpenOpsLoading, setAdminOpenOpsLoading] = useState(false);
  const [agentOpenOps, setAgentOpenOps] = useState([]);
  const [agentOpenOpsLoading, setAgentOpenOpsLoading] = useState(false);
  const initialHomeCache = useMemo(() => readInitialHomeCache(), []);
  const homeCacheAgentKey = useMemo(
    () => resolveHomeDashboardCacheAgentKey(getUserInfo(), dashboardUser),
    [dashboardUser]
  );
  const dashboardIsAdmin = useMemo(() => {
    const roleFromServer = String(
      dashboardUser?.role ?? ""
    )
      .trim()
      .toLowerCase();
    const isAdminFromServer = Boolean(
      dashboardUser?.is_admin
    );
    return isAdminFromServer || ADMIN_ROLES.has(roleFromServer);
  }, [
    dashboardUser?.is_admin,
    dashboardUser?.role,
  ]);
  const openOffersCount = useOpenOffersCount(
    dashboardIsAdmin,
    dashboardUser?.id
  );
  const {
    data: entitlementsPayload,
    status: entitlementsStatus,
    canSearchHotels,
    canSearchPackages,
    canUseDynamicPackages,
    canUseAgentPublicSite,
  } = useAgentEntitlements();
  const { billingStatus, loadedOnce: billingStatusLoaded, reload: reloadBillingStatus } =
    useAgentBillingStatus();
  const {
    data: accountOverviewFromCtx,
    loadedOnce: accountOverviewLoadedOnce,
  } = useAgentAccountOverview();

  const initialBillingExemptionCache = useMemo(
    () => readInitialBillingExemptionCache(),
    []
  );
  const billingExemptionCacheAgentKey = useMemo(
    () =>
      resolveBillingExemptionCacheAgentKey(getUserInfo(), dashboardUser),
    [dashboardUser]
  );
  const [billingExemptionDayCache, setBillingExemptionDayCache] = useState(
    () => initialBillingExemptionCache
  );
  /** After local midnight, allow one quiet network refresh into the new day's cache. */
  const [billingExemptionForceRefresh, setBillingExemptionForceRefresh] =
    useState(false);

  const hadHomeCache = Boolean(
    initialHomeCache?.stats &&
      (initialHomeCache.stats.futureProfit != null ||
        initialHomeCache.stats.availableProfit != null ||
        initialHomeCache.ordersForCard)
  );

  /** null = still loading that metric (card shows —); number = ready. Hydrate from cache for instant paint. */
  const [stats, setStats] = useState(() => ({
    ...EMPTY_STATS,
    ...(initialHomeCache?.stats || {}),
  }));
  const [walletLoading, setWalletLoading] = useState(!hadHomeCache);
  const [ordersLoading, setOrdersLoading] = useState(
    !(hadHomeCache && Array.isArray(initialHomeCache?.ordersForCard))
  );
  /** null = fetch in progress for shared RecentOrdersCard; array = reuse same GET /getRecentOrdersAll as stats */
  const [ordersForCard, setOrdersForCard] = useState(
    () => initialHomeCache?.ordersForCard ?? null
  );
  const [recentFeedErrorType, setRecentFeedErrorType] = useState(null);
  /** SSOT ארנק לדף הבית — לרינדור כרטיס רווח זמין ולדיבוג */
  const [homeWalletSnapshot, setHomeWalletSnapshot] = useState(
    () => initialHomeCache?.homeWalletSnapshot ?? null
  );
  const homeCoreReady = !walletLoading && !ordersLoading;
  const [agentName, setAgentName] = useState("");
  const [agentIdLabel, setAgentIdLabel] = useState("");
  const [publicSiteUrl, setPublicSiteUrl] = useState("");
  /** Only the selected heavy widget mounts — calendar/leaders stay unmounted until chosen. */
  const [activeHeavyTab, setActiveHeavyTab] = useState(HEAVY_TAB_RECENT);
  const accountOverview = accountOverviewFromCtx;
  const statsRef = useRef(stats);
  const ordersRef = useRef(ordersForCard);
  const walletRef = useRef(homeWalletSnapshot);
  statsRef.current = stats;
  ordersRef.current = ordersForCard;
  walletRef.current = homeWalletSnapshot;
  const dashboardHeaderIsAdmin = useMemo(() => {
    const roleFromServer = String(
      accountOverview?.agent?.role ?? dashboardUser?.role ?? ""
    )
      .trim()
      .toLowerCase();
    const isAdminFromServer = Boolean(
      accountOverview?.agent?.is_admin ?? dashboardUser?.is_admin
    );
    return isAdminFromServer || ADMIN_ROLES.has(roleFromServer);
  }, [
    accountOverview?.agent?.is_admin,
    accountOverview?.agent?.role,
    dashboardUser?.is_admin,
    dashboardUser?.role,
  ]);
  const dashboardPackageMissingAssignment = useMemo(() => {
    const rs = String(
      accountOverview?.resolvedPackage?.resolutionStatus ?? ""
    )
      .trim()
      .toLowerCase();
    return rs === "missing_package_assignment";
  }, [accountOverview?.resolvedPackage?.resolutionStatus]);
  /** true after the shared account-overview request settles (so we never use session cache for the package badge). */
  const accountOverviewFetchComplete = accountOverviewLoadedOnce;
  /** One of billing or account-overview has finished loading — safe to render package from server-only inputs. */
  const packageDataReady =
    billingStatusLoaded || accountOverviewFetchComplete;
  const resolvedDisplayedPackage = useMemo(() => {
    const base = resolveAgentDisplayedPackage({
      resolvedPackage: accountOverview?.resolvedPackage,
    });
    const inherited = teamMemberPackageCodeForDisplay(base.packageCode, {
      leadPackageCode: accountOverview?.team?.lead_package_code,
      isAgencyTeam: Boolean(
        accountOverview?.team?.is_team_lead ||
          accountOverview?.team?.lead_package_code === "AGENCY",
      ),
    });
    if (inherited === "AGENCY" && base.packageCode !== "AGENCY") {
      return {
        ...base,
        packageCode: "AGENCY",
        packageNameHe: agentPackageLabelHe("AGENCY"),
      };
    }
    return base;
  }, [accountOverview?.resolvedPackage, accountOverview?.team]);
  const chipTierSuffix = getEntitlementsChipTierCssSuffix({
    status: entitlementsStatus,
    canUseDynamicPackages,
    canSearchHotels,
    canSearchPackages,
    displayedPackageCode: packageDataReady
      ? resolvedDisplayedPackage.packageCode
      : null,
  });
  const chipLineHe = packageDataReady
    ? `חבילה · ${resolvedDisplayedPackage.packageNameHe}`
    : "טוען חבילה...";

  const blockPublicSitePreviewChip = !canUseAgentPublicSite;

  const {
    liveBillingExemptionPayload,
    liveBillingExemptionSource,
  } = useMemo(() => {
    const overview = accountOverview?.billing_exemption;
    const billing = billingStatus?.billingExemption;

    const hasRenderableData = (payload) => {
      if (!payload || typeof payload !== "object") return false;
      const waiver = String(payload.waiver_source || "").trim().toLowerCase();
      if (waiver && waiver !== "none") return true;
      return payload.monthly_sales_threshold_ils != null;
    };

    // Prefer billing/status — home skips exemption on account-overview to avoid double compute.
    if (hasRenderableData(billing)) {
      return {
        liveBillingExemptionPayload: billing,
        liveBillingExemptionSource: "billing_status.billingExemption",
      };
    }
    if (hasRenderableData(overview)) {
      return {
        liveBillingExemptionPayload: overview,
        liveBillingExemptionSource: "account_overview.billing_exemption",
      };
    }
    if (billing) {
      return {
        liveBillingExemptionPayload: billing,
        liveBillingExemptionSource: "billing_status.billingExemption",
      };
    }
    if (overview) {
      return {
        liveBillingExemptionPayload: overview,
        liveBillingExemptionSource: "account_overview.billing_exemption",
      };
    }
    return {
      liveBillingExemptionPayload: null,
      liveBillingExemptionSource: "missing",
    };
  }, [accountOverview?.billing_exemption, billingStatus?.billingExemption]);

  const hasValidBillingExemptionDayCache = Boolean(
    billingExemptionDayCache?.payload
  );

  // Same calendar day: prefer cached snapshot so revisits skip skeleton / network wait.
  const billingExemptionPayload =
    hasValidBillingExemptionDayCache && !billingExemptionForceRefresh
      ? billingExemptionDayCache.payload
      : liveBillingExemptionPayload ||
        billingExemptionDayCache?.payload ||
        null;
  const billingExemptionSource =
    hasValidBillingExemptionDayCache && !billingExemptionForceRefresh
      ? billingExemptionDayCache.source || "daily_cache"
      : liveBillingExemptionPayload
        ? liveBillingExemptionSource
        : billingExemptionDayCache?.source || "missing";

  const billingExemptionLoading =
    !billingExemptionPayload && !billingStatusLoaded;

  // Persist first successful payload of the local day; skip overwrite while today's cache is valid.
  useEffect(() => {
    if (!liveBillingExemptionPayload) return;
    if (hasValidBillingExemptionDayCache && !billingExemptionForceRefresh) {
      return;
    }
    writeBillingExemptionDailyCache(
      billingExemptionCacheAgentKey,
      liveBillingExemptionPayload,
      liveBillingExemptionSource
    );
    const next = readBillingExemptionDailyCache(billingExemptionCacheAgentKey);
    setBillingExemptionDayCache(
      next || {
        dayKey: localCalendarDayKey(),
        savedAt: Date.now(),
        payload: liveBillingExemptionPayload,
        source: liveBillingExemptionSource,
      }
    );
    setBillingExemptionForceRefresh(false);
  }, [
    billingExemptionCacheAgentKey,
    billingExemptionForceRefresh,
    hasValidBillingExemptionDayCache,
    liveBillingExemptionPayload,
    liveBillingExemptionSource,
  ]);

  // Quiet refresh at next local midnight if the dashboard stays open.
  useEffect(() => {
    let cancelled = false;
    let timerId = null;

    const schedule = () => {
      const delay = msUntilNextLocalMidnight();
      timerId = window.setTimeout(() => {
        if (cancelled) return;
        clearBillingExemptionDailyCache(billingExemptionCacheAgentKey);
        setBillingExemptionDayCache(null);
        setBillingExemptionForceRefresh(true);
        try {
          reloadBillingStatus?.();
        } catch {
          /* ignore */
        }
        schedule();
      }, delay);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, [billingExemptionCacheAgentKey, reloadBillingStatus]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const p = billingExemptionPayload || {};
    console.log("[AGENT_BILLING_EXEMPTION_UI_TRACE]", {
      widget: "dashboard_chip",
      endpoint: billingExemptionSource,
      from_daily_cache:
        hasValidBillingExemptionDayCache && !billingExemptionForceRefresh,
      sales_month: p.sales_month ?? null,
      included_total_ils: p.monthly_sales_current_month_ils ?? null,
      threshold: p.monthly_sales_threshold_ils ?? null,
      progress_percent: p.monthly_sales_progress_percent ?? null,
      calculation_mode: p.calculation_mode ?? null,
      calculation_version: p.calculation_version ?? null,
      qualification_hash: p.qualification_hash ?? null,
    });
  }, [
    billingExemptionForceRefresh,
    billingExemptionPayload,
    billingExemptionSource,
    hasValidBillingExemptionDayCache,
  ]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[AGENT_SITE_ACCESS_GUARD]", {
      packageCode:
        entitlementsPayload?.package?.code ??
        accountOverview?.subscription?.package_code ??
        null,
      canUseAgentPublicSite,
      route: "/p/dashboard/home",
    });
  }, [
    accountOverview?.subscription?.package_code,
    canUseAgentPublicSite,
    entitlementsPayload?.package?.code,
  ]);

  // Soft-revalidate: keep cached KPIs visible; update each metric as its request lands.
  useEffect(() => {
    let cancelled = false;
    setRecentFeedErrorType(null);
    if (!hadHomeCache) {
      setWalletLoading(true);
      setOrdersLoading(true);
    }

    const userInfo = getUserInfo();
    const headers = buildRecentOrdersHomeFeedHeaders();
    const homeFeedIsAdmin = resolveHomeFeedViewerIsAdmin();
    const sessionId = resolveHomeFeedSessionId();
    const ordersBody = { limit: HOME_RECENT_ORDERS_LIMIT };
    if (sessionId) {
      ordersBody.sessionID = sessionId;
    }

    const walletBody =
      userInfo?.email && String(userInfo.email).trim() !== ""
        ? { email: String(userInfo.email).trim() }
        : {};

    const persistCache = () => {
      writeHomeDashboardCache(homeCacheAgentKey, {
        stats: statsRef.current,
        ordersForCard: ordersRef.current,
        homeWalletSnapshot: walletRef.current,
      });
    };

    // Orders feed (independent of wallet)
    (async () => {
      try {
        const ordersRes = await performPostFetch(
          resolveApiUrl(RECENT_ORDERS_HOME_FEED_ENDPOINT),
          ordersBody,
          false,
          { headers }
        );
        if (cancelled) return;
        const feedParsed = parseRecentOrdersHomeFeedResponse(ordersRes);
        const orders = feedParsed.orders;
        if (!feedParsed.ok) {
          setRecentFeedErrorType(
            feedParsed.forbidden ? "forbidden" : "error"
          );
        }
        traceRecentOrdersHomeFeed({
          role: dashboardUser?.role ?? null,
          endpoint: RECENT_ORDERS_HOME_FEED_ENDPOINT,
          rowsCount: orders.length,
          filteredCount: orders.length,
          isAdmin: homeFeedIsAdmin,
          httpStatus: ordersRes?.status ?? null,
          feedOk: feedParsed.ok,
          feedError: feedParsed.error,
        });

        const pendingOrders = orders.filter((order) => {
          if (isLikelyLdsOrderRow(order)) {
            return !isLdsOrderBusinessSettled(order);
          }
          return order?.status === "pending" || order?.paid === false;
        }).length;

        const openOrders = orders.filter(
          (order) => order?.status === "open" || order?.cancelled === false
        ).length;

        setStats((prev) => {
          const next = { ...prev, pendingOrders, openOrders };
          statsRef.current = next;
          return next;
        });
        setOrdersForCard(orders);
        ordersRef.current = orders;
        persistCache();
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching dashboard recent orders:", error);
        if (!hadHomeCache) {
          setOrdersForCard([]);
          setStats((prev) => ({
            ...prev,
            pendingOrders: 0,
            openOrders: 0,
          }));
        }
        setRecentFeedErrorType("error");
        traceRecentOrdersHomeFeed({
          role: dashboardUser?.role ?? null,
          endpoint: RECENT_ORDERS_HOME_FEED_ENDPOINT,
          rowsCount: 0,
          filteredCount: 0,
          isAdmin: resolveHomeFeedViewerIsAdmin(),
          fetchException: error?.message ?? String(error),
        });
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    })();

    // Wallet KPIs — progressive: each card updates when its endpoint returns
    let walletPending = 3;
    const markWalletPartDone = () => {
      walletPending -= 1;
      if (walletPending <= 0 && !cancelled) setWalletLoading(false);
    };

    const mergeWalletSummary = (patch) => {
      setHomeWalletSnapshot((prev) => {
        const prevSummary = prev?.walletSummary || {};
        const next = {
          ...(prev || {}),
          ...patch,
          walletSummary: {
            ...prevSummary,
            ...(patch.walletSummary || {}),
          },
        };
        walletRef.current = next;
        return next;
      });
      setStats((prevStats) => {
        const summary = {
          ...(walletRef.current?.walletSummary || {}),
          ...(patch.walletSummary || {}),
        };
        const nextStats = {
          ...prevStats,
          futureProfit:
            summary.futureTotal != null
              ? summary.futureTotal
              : prevStats.futureProfit,
          availableProfit:
            summary.availableTotal != null
              ? summary.availableTotal
              : prevStats.availableProfit,
          historyProfit:
            summary.historyTotal != null
              ? summary.historyTotal
              : prevStats.historyProfit,
        };
        statsRef.current = nextStats;
        persistCache();
        return nextStats;
      });
    };

    getFutureOrdersCommission(walletBody)
      .then((fRes) => {
        if (cancelled) return;
        const futureLines = Array.isArray(fRes?.data) ? fRes.data : [];
        const futureTotal = sumCommissionsFromLines(futureLines).total;
        if (import.meta.env.DEV) {
          traceWalletSsotSource("futureOrdersCommission", futureLines);
        }
        mergeWalletSummary({ walletSummary: { futureTotal } });
      })
      .catch(() => {
        if (cancelled || hadHomeCache) return;
        mergeWalletSummary({ walletSummary: { futureTotal: 0 } });
      })
      .finally(markWalletPartDone);

    Promise.all([
      getAvailableOrdersCommission(walletBody),
      getFinanceData(walletBody),
    ])
      .then(([aRes, finRes]) => {
        if (cancelled) return;
        const avBody = aRes?.data;
        const finData = finRes?.data;
        const possibleProfit =
          finData?.possibleProfit ?? finData?.data?.possibleProfit;
        const summary = buildWalletSummary({
          futureLines: [],
          availableBuckets: avBody,
          historyLines: [],
          possibleProfit,
        });
        const flattenedAvailableLines =
          flattenAvailableBucketsExcludingSystemBalance(avBody);
        const { total: availableRowsTotal } =
          sumCommissionsFromLines(flattenedAvailableLines);

        if (import.meta.env.DEV) {
          console.log("[AVAILABLE_CARD_BINDING_TRACE]", {
            possibleProfit,
            availableBuckets: avBody,
            flattenedAvailableLines,
            availableRowsTotal,
            walletSummary: { availableTotal: summary.availableTotal },
            cardDisplayedValue: summary.availableTotal,
          });
          traceWalletSsotSource(
            "availableOrdersCommission",
            flattenedAvailableLines
          );
        }

        console.log("[HOME_AVAILABLE_CARD_TRACE]", {
          phase: "DashboardPage.fetchWallet.available",
          componentName: "DashboardPage",
          sessionIdPresent: Boolean(sessionId),
          possibleProfit,
          availableRowsTotal,
          walletSummaryAvailableTotal: summary.availableTotal,
        });

        mergeWalletSummary({
          possibleProfit,
          availableBuckets: avBody,
          flattenedAvailableLines,
          availableRowsTotal,
          walletSummary: {
            availableTotal: summary.availableTotal,
            withdrawableTotal: summary.withdrawableTotal,
          },
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[HOME_AVAILABLE_CARD_TRACE]", {
          phase: "DashboardPage.fetchWallet.available.error",
          componentName: "DashboardPage",
          err: err?.message ?? String(err),
        });
        if (!hadHomeCache) {
          mergeWalletSummary({
            walletSummary: { availableTotal: 0, withdrawableTotal: 0 },
          });
        }
      })
      .finally(markWalletPartDone);

    getOrdersCommissionHistory(walletBody)
      .then((hRes) => {
        if (cancelled) return;
        const histLines = Array.isArray(hRes?.data) ? hRes.data : [];
        const historyTotal = sumCommissionsFromLines(histLines).total;
        if (import.meta.env.DEV) {
          traceWalletSsotSource("ordersCommissionHistory", histLines);
        }
        mergeWalletSummary({ walletSummary: { historyTotal } });
      })
      .catch(() => {
        if (cancelled || hadHomeCache) return;
        mergeWalletSummary({ walletSummary: { historyTotal: 0 } });
      })
      .finally(markWalletPartDone);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount soft-revalidate once per visit
  }, [homeCacheAgentKey]);

  // שם + מזהה סוכן מאותו מקור כמו כותרת הברכה (UserInfo / login)
  useEffect(() => {
    try {
      const userInfo = getUserInfo();
      if (userInfo) {
        const name = `${userInfo.firstname || ""} ${userInfo.lastname || ""}`.trim();
        setAgentName(name || "סוכן");
        const rawId = userInfo.id ?? userInfo.agentId;
        const idStr =
          rawId != null && String(rawId).trim() !== ""
            ? String(rawId).trim()
            : "";
        setAgentIdLabel(idStr);
      } else {
        setAgentName("סוכן");
        setAgentIdLabel("");
      }
    } catch (error) {
      console.error("Error getting agent name:", error);
      setAgentName("סוכן");
      setAgentIdLabel("");
    }
  }, []);

  // Reuse shared AgentAccountOverviewProvider — no second account-overview round-trip.
  useEffect(() => {
    let cancelled = false;

    const emitViewMySiteTrace = (payload) => {
      if (!import.meta.env.DEV) return;
      // eslint-disable-next-line no-console
      console.info("[VIEW_MY_SITE_URL_TRACE]", payload);
    };

    const resolveViewMySiteUrl = async (agentCandidate, hint = "unknown") => {
      const resolved = await buildAgentPublicSiteUrl(agentCandidate, {
        resolveByAgentId: resolvePublicSiteRefByAgentId,
      });
      emitViewMySiteTrace({
        agentId: resolved?.agentId ?? null,
        agentEmail: resolved?.agentEmail ?? null,
        publicSiteId: resolved?.publicSiteId ?? null,
        publicSlug: resolved?.publicSlug ?? null,
        publicTokenExists: Boolean(resolved?.publicTokenExists),
        generatedUrl: resolved?.generatedUrl ?? null,
        source: `${hint}:${resolved?.source || "unknown"}`,
      });
      return resolved?.generatedUrl || "";
    };

    const resolveUrl = async () => {
      const agent = accountOverviewFromCtx?.agent || {};
      const primaryUrl = await resolveViewMySiteUrl(agent, "overview_ctx_agent");
      if (cancelled) return;
      if (primaryUrl) {
        setPublicSiteUrl(primaryUrl);
        return;
      }
      const sessionAgent = {
        ...(dashboardUser || {}),
        ...(getUserInfo() || {}),
      };
      const fallbackUrl = await resolveViewMySiteUrl(sessionAgent, "session_agent");
      if (cancelled) return;
      setPublicSiteUrl(fallbackUrl);
    };

    if (!accountOverviewLoadedOnce) return undefined;
    void resolveUrl();
    return () => {
      cancelled = true;
    };
  }, [accountOverviewFromCtx, accountOverviewLoadedOnce, dashboardUser]);

  useEffect(() => {
    // TODO: remove [AGENT_PACKAGE_BADGE_TRACE] after dashboard package badge is validated in production.
    const sourceUsed = packageDataReady
      ? resolvedDisplayedPackage.source
      : "loading";
    console.debug("[AGENT_PACKAGE_BADGE_TRACE]", {
      screen: "dashboard_home",
      billingStatusLoaded: billingStatusLoaded,
      accountOverviewLoaded: accountOverviewFetchComplete,
      sourceUsed,
      billing_effective_package:
        billingStatus?.effectivePackage ?? billingStatus?.effective_package,
      billing_package_code:
        billingStatus?.packageCode ?? billingStatus?.package_code,
      overview_agent_package: accountOverview?.agent?.package,
      overview_agent_package_id: accountOverview?.agent?.package_id,
      overview_subscription_package_code:
        accountOverview?.subscription?.package_code,
      overview_subscription_package_id:
        accountOverview?.subscription?.package_id,
      dashboardUser_package: dashboardUser?.package,
      dashboardUser_package_id: dashboardUser?.package_id,
      resolvedDisplayedPackage,
    });
  }, [
    accountOverview?.agent,
    accountOverview?.subscription,
    accountOverviewFetchComplete,
    billingStatus?.effectivePackage,
    billingStatus?.effective_package,
    billingStatus?.packageCode,
    billingStatus?.package_code,
    billingStatusLoaded,
    dashboardUser?.package,
    dashboardUser?.package_id,
    packageDataReady,
    resolvedDisplayedPackage.packageCode,
    resolvedDisplayedPackage.source,
  ]);

  useEffect(() => {
    if (!dashboardHeaderIsAdmin) {
      setAdminOpenOps([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setAdminOpenOpsLoading(true);
      try {
        const data = await fetchAdminOperationRequests();
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        const open = items.filter((item) => {
          const st = String(item?.status || "").trim().toLowerCase();
          return !OPS_TERMINAL.has(st);
        });
        setAdminOpenOps(open.slice(0, 8));
      } catch {
        if (!cancelled) setAdminOpenOps([]);
      } finally {
        if (!cancelled) setAdminOpenOpsLoading(false);
      }
    };
    void load();
    const onChanged = () => void load();
    window.addEventListener("operationRequestUnreadChanged", onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("operationRequestUnreadChanged", onChanged);
    };
  }, [dashboardHeaderIsAdmin]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setAgentOpenOpsLoading(true);
      try {
        const data = await fetchMyOperationRequests();
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        const open = items.filter((item) => {
          const st = String(item?.status || "").trim().toLowerCase();
          return !OPS_TERMINAL.has(st);
        });
        open.sort((a, b) => {
          const ua = hasUnreadAdminReplyFromItem(a) ? 1 : 0;
          const ub = hasUnreadAdminReplyFromItem(b) ? 1 : 0;
          return ub - ua;
        });
        setAgentOpenOps(open.slice(0, 8));
      } catch {
        if (!cancelled) setAgentOpenOps([]);
      } finally {
        if (!cancelled) setAgentOpenOpsLoading(false);
      }
    };
    void load();
    const intervalId = window.setInterval(() => void load(), 10000);
    const onChanged = () => void load();
    window.addEventListener("operationRequestUnreadChanged", onChanged);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("operationRequestUnreadChanged", onChanged);
    };
  }, []);

  const adminOpenOpsCount = useMemo(() => {
    const fromHub = Number(remindersHub?.summary?.operation_open);
    if (Number.isFinite(fromHub) && fromHub >= 0) return fromHub;
    return adminOpenOps.length;
  }, [adminOpenOps.length, remindersHub?.summary?.operation_open]);

  const agentUnreadOpsCount = useMemo(
    () => agentOpenOps.filter((item) => hasUnreadAdminReplyFromItem(item)).length,
    [agentOpenOps]
  );

  const openAgentOpsThread = (item) => {
    if (item) {
      void navigateToCrmOpsDocket(navigate, item);
      return;
    }
    navigate(buildCrmOpsDocketPath());
  };

  const showTopAgentsOfMonth =
    !TOP_AGENTS_OF_MONTH_ADMIN_ONLY || dashboardHeaderIsAdmin;

  useEffect(() => {
    if (!showTopAgentsOfMonth && activeHeavyTab === HEAVY_TAB_LEADERS) {
      setActiveHeavyTab(HEAVY_TAB_RECENT);
    }
  }, [showTopAgentsOfMonth, activeHeavyTab]);

  const heavyTabs = useMemo(() => {
    const tabs = [
      { key: HEAVY_TAB_RECENT, label: "הזמנות אחרונות במערכת" },
      { key: HEAVY_TAB_CALENDAR, label: "יומן הזמנות חודשי" },
    ];
    if (showTopAgentsOfMonth) {
      tabs.push({ key: HEAVY_TAB_LEADERS, label: "המובילים של החודש" });
    }
    return tabs;
  }, [showTopAgentsOfMonth]);

  const dashboardTasks = useMemo(() => {
    const tasks = [];
    if ((cxlDropRisk.summary?.count || 0) > 0) {
      const minDays = cxlDropRisk.summary.minDaysLeft;
      const daysBit =
        minDays === 0
          ? "היום האחרון לביטול חינם"
          : minDays === 1
            ? "נותר יום אחד"
            : `נותרו עד ${minDays} ימים לביטול חינם`;
      tasks.push({
        id: "cxl-drop-risk",
        type: "urgent",
        title: "מלונות בסיכון ביטול",
        description: cxlDropRisk.viewerIsOps
          ? `${cxlDropRisk.summary.count} הזמנות לא משולמות — ${daysBit}. בלי תשלום ייפלו ולא ניתן לשחזר.`
          : `${daysBit}. בלי תשלום ההזמנה תיפול ולא ניתן לשחזר.`,
        count: cxlDropRisk.summary.count,
        action: () => navigate(buildHotelCxlDropRiskOrdersPath()),
      });
    }
    const unreadFeed = Number(remindersHub?.summary?.admin_feed_unread) || 0;
    if (unreadFeed > 0) {
      tasks.push({
        id: "reminders",
        type: "reminder",
        title: "הודעות מערכת",
        count: unreadFeed,
        action: () => navigate("/p/dashboard/reminders"),
      });
    }
    return tasks;
  }, [
    cxlDropRisk.summary,
    cxlDropRisk.viewerIsOps,
    navigate,
    remindersHub?.summary?.admin_feed_unread,
  ]);

  return (
    <div className="dashboard-page" dir="rtl">
      <div className="dashboard-page__container">
        {/* Header */}
        <header className="dashboard-page__header">
          <div className="dashboard-page__header-content">
            <div className="dashboard-page__title-row">
              <h1 className="dashboard-page__title">
                ברוך הבא{agentName ? `, ${agentName}` : ""}
              </h1>
              {dashboardHeaderIsAdmin ? (
                <span className="dashboard-page__admin-badge">אדמין</span>
              ) : null}
            </div>
            {agentIdLabel ? (
              <p className="dashboard-page__agent-code">
                קוד סוכן: {agentIdLabel}
              </p>
            ) : null}
            {dashboardHeaderIsAdmin &&
            packageDataReady &&
            dashboardPackageMissingAssignment ? (
              <Alert
                className="dashboard-page__package-assignment-alert"
                type="warning"
                showIcon
                message="לסוכן אין חבילה מוגדרת"
              />
            ) : null}
            <div
              className="dashboard-page__header-chips"
              role="group"
              aria-label="סטטוס חשבון"
            >
              <button
                type="button"
                className={`upper-nav-dash-chip upper-nav-dash-chip--plan upper-nav-dash-chip--plan-${chipTierSuffix}${
                  !packageDataReady
                    ? " dashboard-page__package-chip--loading"
                    : ""
                }`}
                onClick={() => navigate("/p/dashboard/management")}
                aria-busy={!packageDataReady}
              >
                {chipLineHe}
              </button>
              <button
                type="button"
                className={`upper-nav-dash-chip upper-nav-dash-chip--offers ${
                  openOffersCount > 0
                    ? "upper-nav-dash-chip--offers-active"
                    : "upper-nav-dash-chip--offers-zero"
                }`}
                onClick={() => navigate("/p/dashboard/drafts?status=open")}
              >
                {openOffersCount} הצעות פתוחות
              </button>
              {dashboardHeaderIsAdmin ? (
                <button
                  type="button"
                  className="dashboard-page__ops-open-chip"
                  onClick={() => navigate("/p/dashboard/admin/agent-mail")}
                >
                  אשף מייל לסוכנים
                </button>
              ) : null}
              {dashboardHeaderIsAdmin ? (
                <button
                  type="button"
                  className={`dashboard-page__ops-open-chip${
                    adminOpenOpsCount > 0
                      ? " dashboard-page__ops-open-chip--active"
                      : ""
                  }`}
                  onClick={() =>
                    navigate("/p/dashboard/admin/operation-requests")
                  }
                >
                  {adminOpenOpsCount} שיחות פתוחות
                </button>
              ) : null}
              {agentOpenOps.length > 0 || agentUnreadOpsCount > 0 ? (
                <button
                  type="button"
                  className={`dashboard-page__ops-open-chip${
                    agentUnreadOpsCount > 0
                      ? " dashboard-page__ops-open-chip--unread"
                      : ""
                  }`}
                  onClick={() =>
                    openAgentOpsThread(
                      agentOpenOps.find((item) =>
                        hasUnreadAdminReplyFromItem(item)
                      ) || agentOpenOps[0]
                    )
                  }
                >
                  {agentUnreadOpsCount > 0
                    ? agentUnreadOpsCount === 1
                      ? "תשובה מאופרציה"
                      : `${agentUnreadOpsCount} תשובות מאופרציה`
                    : `${agentOpenOps.length} שיחות עם אופרציה`}
                </button>
              ) : null}
              <button
                type="button"
                className="dashboard-page__op-request-chip"
                onClick={() => openOperationRequest()}
              >
                פנייה לאופרציה
              </button>
              {blockPublicSitePreviewChip ? (
                <Tooltip title="זמין בחבילת PRO ומעלה">
                  <button
                    type="button"
                    className="dashboard-page__public-site-link dashboard-page__public-site-link--locked"
                    onClick={() =>
                      navigateAgentPackageUpgrade(navigate, "dashboard_public_site_preview")
                    }
                    aria-label="צפה באתר שלי — זמין בחבילת PRO ומעלה"
                  >
                    <LockOutlined aria-hidden />
                    <span>צפה באתר שלי</span>
                  </button>
                </Tooltip>
              ) : publicSiteUrl ? (
                <a
                  className="dashboard-page__public-site-link"
                  href={publicSiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="צפה באתר שלי"
                >
                  <span aria-hidden="true">🌐</span>
                  <span>צפה באתר שלי</span>
                </a>
              ) : null}
            </div>
          </div>
        </header>

        <HotelCxlDropRiskBanner
          variant="dashboard"
          summary={cxlDropRisk.summary}
          viewerIsOps={cxlDropRisk.viewerIsOps}
          loading={cxlDropRisk.loading}
        />

        {dashboardHeaderIsAdmin ? (
          <section
            className="dashboard-page__ops-open-section"
            aria-label="שיחות אופרציה פתוחות"
          >
            <div className="dashboard-page__ops-open-head">
              <h2 className="dashboard-page__ops-open-title">
                שיחות אופרציה פתוחות
              </h2>
              <button
                type="button"
                className="dashboard-page__ops-open-all"
                onClick={() =>
                  navigate("/p/dashboard/admin/operation-requests")
                }
              >
                לכל השיחות
              </button>
            </div>
            {adminOpenOpsLoading && !adminOpenOps.length ? (
              <p className="dashboard-page__ops-open-empty">טוען שיחות…</p>
            ) : adminOpenOps.length ? (
              <div className="dashboard-page__ops-open-list">
                {adminOpenOps.map((item) => {
                  const id = String(item?.id || "").trim();
                  const orderNo =
                    item?.displayOrderNumber ||
                    item?.display_order_number ||
                    item?.orderId ||
                    "—";
                  const agentNameOnCard = String(
                    item?.agentName || item?.agent_name || ""
                  ).trim();
                  const agentId = item?.agentId ?? item?.agent_id;
                  const agentLabel =
                    agentNameOnCard ||
                    (agentId != null && String(agentId).trim()
                      ? `סוכן #${String(agentId).trim()}`
                      : "");
                  const extraCount = Array.isArray(
                    item?.additionalAgents || item?.additional_agents
                  )
                    ? (item?.additionalAgents || item?.additional_agents).length
                    : 0;
                  const waitingOnOps = hasUnreadAgentReplyFromItem(item);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`dashboard-page__ops-open-card${
                        waitingOnOps
                          ? " dashboard-page__ops-open-card--unread"
                          : ""
                      }`}
                      onClick={() =>
                        navigate(
                          `/p/dashboard/admin/operation-requests?requestId=${encodeURIComponent(id)}`
                        )
                      }
                    >
                      <span className="dashboard-page__ops-open-card-order">
                        הזמנה {orderNo}
                      </span>
                      <span className="dashboard-page__ops-open-card-type">
                        {item?.requestTypeLabel ||
                          item?.request_type_label ||
                          "פנייה"}
                      </span>
                      {agentLabel ? (
                        <span className="dashboard-page__ops-open-card-agent">
                          סוכן: {agentLabel}
                          {extraCount > 0 ? ` (+${extraCount})` : ""}
                        </span>
                      ) : null}
                      {waitingOnOps ? (
                        <span className="dashboard-page__ops-open-card-badge">
                          הסוכן ממתין לתשובה
                        </span>
                      ) : null}
                      <OperationRequestHandlingTimer request={item} compact />
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="dashboard-page__ops-open-empty">
                אין שיחות פתוחות כרגע
              </p>
            )}
          </section>
        ) : null}

        <section
          className="dashboard-page__ops-open-section"
          aria-label="השיחות שלי עם אופרציה"
        >
          <div className="dashboard-page__ops-open-head">
            <h2 className="dashboard-page__ops-open-title">
              השיחות שלי עם אופרציה
            </h2>
            <button
              type="button"
              className="dashboard-page__ops-open-all"
              onClick={() => navigate(buildCrmOpsDocketPath())}
            >
              לכל השיחות
            </button>
          </div>
          {agentOpenOpsLoading && !agentOpenOps.length ? (
            <p className="dashboard-page__ops-open-empty">טוען שיחות…</p>
          ) : agentOpenOps.length ? (
            <div className="dashboard-page__ops-open-list">
              {agentOpenOps.map((item) => {
                const id = String(item?.id || "").trim();
                const orderNo =
                  item?.displayOrderNumber ||
                  item?.display_order_number ||
                  item?.orderId ||
                  "—";
                const unread = hasUnreadAdminReplyFromItem(item);
                return (
                  <button
                    key={id}
                    type="button"
                    className={`dashboard-page__ops-open-card${
                      unread ? " dashboard-page__ops-open-card--unread" : ""
                    }`}
                    onClick={() => openAgentOpsThread(item)}
                  >
                    <span className="dashboard-page__ops-open-card-order">
                      הזמנה {orderNo}
                    </span>
                    <span className="dashboard-page__ops-open-card-type">
                      {item?.requestTypeLabel ||
                        item?.request_type_label ||
                        "פנייה"}
                    </span>
                    {unread ? (
                      <span className="dashboard-page__ops-open-card-badge">
                        תשובה חדשה מאופרציה
                      </span>
                    ) : (
                      <span className="dashboard-page__ops-open-card-agent">
                        ממתין לאופרציה
                      </span>
                    )}
                    <OperationRequestHandlingTimer request={item} compact />
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="dashboard-page__ops-open-empty">
              אין שיחות פתוחות כרגע — פנייה חדשה נפתחת מכפתור ״פנייה לאופרציה״
            </p>
          )}
        </section>

        {/* KPI Stats Row */}
        <section className="dashboard-page__stats-section">
          <DashboardStats
            stats={stats}
            loading={false}
            homeWalletSnapshot={homeWalletSnapshot}
            deferHrCommission
          />
        </section>

        <section className="dashboard-page__billing-exemption-section">
          <BillingExemptionCard
            title="התקדמות מכירות חודשית"
            payload={billingExemptionPayload}
            source={billingExemptionSource}
            loading={billingExemptionLoading}
          />
        </section>

        {/* Heavy widgets — one active tab mounts at a time */}
        <section
          className="dashboard-page__heavy-section"
          aria-label="תצוגות דשבורד"
        >
          <div
            className="dashboard-page__heavy-tabs"
            role="tablist"
            aria-label="בחירת תצוגה"
          >
            {heavyTabs.map((tab) => {
              const isActive = activeHeavyTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  id={`dashboard-heavy-tab-${tab.key}`}
                  aria-selected={isActive}
                  aria-controls={`dashboard-heavy-panel-${tab.key}`}
                  tabIndex={isActive ? 0 : -1}
                  className={`dashboard-page__heavy-tab${
                    isActive ? " dashboard-page__heavy-tab--active" : ""
                  }`}
                  onClick={() => setActiveHeavyTab(tab.key)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="dashboard-page__heavy-panel-wrap">
            {activeHeavyTab === HEAVY_TAB_RECENT ? (
              <div
                className="dashboard-page__panel dashboard-page__panel--orders"
                role="tabpanel"
                id={`dashboard-heavy-panel-${HEAVY_TAB_RECENT}`}
                aria-labelledby={`dashboard-heavy-tab-${HEAVY_TAB_RECENT}`}
              >
                <RecentOrdersCard
                  maxItems={15}
                  useDashboardOrders
                  ordersFromDashboard={ordersForCard}
                  dashboardStatsLoading={ordersLoading}
                  feedErrorType={recentFeedErrorType}
                />
              </div>
            ) : null}

            {activeHeavyTab === HEAVY_TAB_CALENDAR ? (
              <div
                className="dashboard-page__panel dashboard-page__panel--calendar"
                role="tabpanel"
                id={`dashboard-heavy-panel-${HEAVY_TAB_CALENDAR}`}
                aria-labelledby={`dashboard-heavy-tab-${HEAVY_TAB_CALENDAR}`}
              >
                <OrdersCalendarCard ordersFromDashboard={ordersForCard} />
              </div>
            ) : null}

            {activeHeavyTab === HEAVY_TAB_LEADERS && showTopAgentsOfMonth ? (
              <div
                className="dashboard-page__panel dashboard-page__panel--leaders"
                role="tabpanel"
                id={`dashboard-heavy-panel-${HEAVY_TAB_LEADERS}`}
                aria-labelledby={`dashboard-heavy-tab-${HEAVY_TAB_LEADERS}`}
              >
                <TopAgentsOfMonthCard readyToFetch={homeCoreReady} />
              </div>
            ) : null}
          </div>
        </section>

        {/* Bottom Section - My ducats + Tasks */}
        <section className="dashboard-page__tasks-section">
          <div className="dashboard-page__tasks-grid">
            <DashboardMyDucatsCard deferMs={HOME_SECONDARY_DEFER_MS} />
            <DashboardTasksCard tasks={dashboardTasks} />
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
